import React, { useEffect, useRef, useState } from 'react';
import * as d3 from "d3";
import Modal from 'react-modal';
//css
import './TimelineElement.css';
//components
import ImageComponent from '../../../helpers/ImageComponent';
import RequestResponseModal from '../requestResponseModal/RequestResponseModal';
//assets
import capturedIcon from '../../../assets/captured.png';
import authorizedIcon from '../../../assets/authorized.png';
import tokenizedIcon from '../../../assets/tokenized.png';
import { HiOutlineReceiptRefund } from "react-icons/hi";
import { MdOutlineCancelPresentation } from "react-icons/md";
import { MdOutlineRocketLaunch } from "react-icons/md";
//helpers
import { getSymbolFromCurrency } from '../../../config/currencyConfig';
import { showAmount } from '../../../config/paymentConfig';
import ToolTip from '../../../helpers/ToolTip';

const customStyles = {
    content: {
        maxWidth: 'fit-content',
        margin: '0 auto',
        top: '100px',
        border: '2px solid gainsboro',
    },
};

export default function TimelineElement({ timeline, parentIndex }) {
    const paymentRefs = useRef([]);
    const successBorderRefs = useRef([]);
    const failureBorderRefs = useRef([]);

    const [showTransactionDetails, setShowTransactionDetails] = useState(false);
    const [transactionsRequest, setTransactionRequest] = useState("");
    const [transactionsResponse, setTransactionResponse] = useState("");
    const [requestMetadata, setRequestMetadata] = useState("");
    const [responseMetadata, setResponseMetadata] = useState("");

    const showTransactionModal = (transactionDetails) => {
        const transReq = { ...transactionDetails.request };
        const transResp = { ...transactionDetails.response };
        setTransactionRequest(transReq);
        setTransactionResponse(transResp);
        setRequestMetadata(transReq['metadata']);
        setResponseMetadata(transResp['metadata'])
        setShowTransactionDetails(true);
    }

    const closeModal = () => {
        setShowTransactionDetails(false);
    }

    useEffect(() => {
        paymentRefs.current.forEach((ref, index) => {
            d3.select(ref)
                .transition()
                .delay(Number("" + parentIndex + index) * 200)
                .duration(50)
                .style("transition-delay", index * 1 + "s")
                .style("opacity", 1)
                .on("end", function showTopBorder() {
                    if (parentIndex !== 0) {
                        d3.select(`.timeline${parentIndex}`)
                            .style("border-top", "1px solid #CFC6C6")
                            .style("padding-top", "1em")
                            .style("margin-left", "15px")
                    }
                    if (parentIndex === 1) {
                        d3.select(".more-button")
                            .style("opacity", 1)
                    }
                })
        })
    }, [parentIndex])

    useEffect(() => {
        successBorderRefs.current.forEach((ref, index) => {
            const height = paymentRefs.current[index]?.clientHeight;
            d3.select(ref)
                .transition()
                .delay(Number("" + parentIndex + index) * 200)
                .style("height", (height - 30) + "px")
                .style("transition-delay", index * 0.5 + "s")
                .style("border-left-color", "green")
        })
    }, [parentIndex])

    useEffect(() => {
        failureBorderRefs.current.forEach((ref, index) => {
            const height = paymentRefs.current[index]?.clientHeight;
            d3.select(ref)
                .transition()
                .delay(Number("" + parentIndex + index) * 200)
                .style("height", (height - 30) + "px")
                .style("transition-delay", index * 0.5 + "s")
                .style("border-left-color", "red")
        })
    }, [parentIndex])

    const getIcon = (stage) => {
        switch (stage) {
            case 'CAPTURED':
                return <ImageComponent sourceUrl={capturedIcon} details="capturedIcon" width="20px" height="20px" />;
            case 'AUTHROIZED':
                return <ImageComponent sourceUrl={authorizedIcon} details="authorizedIcon" width="20px" height="20px" />;
            case 'TOKENIZED':
                return <ImageComponent sourceUrl={tokenizedIcon} details="tokenizedIcon" width="20px" height="20px" />;
            case 'REFUNDED':
                return <HiOutlineReceiptRefund size="20px" style={{ marginRight: '5px', marginLeft: '5px' }} />;
            case 'CANCELLED':
                return <MdOutlineCancelPresentation size="20px" style={{ marginRight: '5px', marginLeft: '5px' }} />;
            default:
                return '';
        }
    };

    const operationTypes = {
        CAPTURED: "Captured",
        AUTHROIZED: "Authorized",
        TOKENIZED: "Tokenized",
        REFUNDED: "Refunded",
        CANCELLED: "Cancelled",
        STARTED: "Started"
    }

    const failedOperationTypes = {
        CAPTURED: "Capture Failed",
        AUTHROIZED: "Authorization Failed",
        TOKENIZED: "Tokenization Failed",
        REFUNDED: "Refund Failed",
        CANCELLED: "Cancellation Failed"
    }

    const getDate = (epochTime) => {
        var options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true, timeZone: 'UTC' };
        var d = new Date(epochTime * 1000);
        d = d.toLocaleString("en-US", options).replace(" at", "");
        return d;
    }

    return (
        <>
            {timeline.map((event, index) =>
                <div key={index} className='timeline-element' ref={(el) => paymentRefs.current[index] = el}>
                    {event.operationType === "STARTED"
                        ?
                        <div className='operation-type'>
                            <MdOutlineRocketLaunch size="20px" style={{ marginRight: '5px', marginLeft: '5px' }} />
                            <span className='operation-stage'>Payment Started</span>
                        </div>
                        :
                        <>
                            <div className='operation-type'>
                                {getIcon(event.operationType)}
                                {event.transactionStatus === "success"
                                    ?
                                    <span className='operation-stage'>{`Payment ${operationTypes[event.operationType]}`}</span>
                                    :
                                    <span className='operation-stage failed-operation-stage'>{`Payment ${failedOperationTypes[event.operationType]}`}</span>}
                            </div>
                            <div className="timeline-section">
                                {event.transactionStatus === "success"
                                    ?
                                    <div className='timeline-left-border border-success' ref={(el) => successBorderRefs.current[index] = el}></div>
                                    :
                                    <div className='timeline-left-border border-fail' ref={(el) => failureBorderRefs.current[index] = el}></div>
                                }
                                <div className='timeline-content'>
                                    <span className='decorate-color'>Via: {event.transactionSource}</span>
                                    <span>
                                        <span>Transaction ID: <span className='decorate-color transaction-id' onClick={() => showTransactionModal(event)}>{event.transactionId}</span></span>
                                        <Modal
                                            isOpen={showTransactionDetails}
                                            onRequestClose={closeModal}
                                            style={customStyles}
                                            contentLabel="Transaction Modal"
                                        >
                                            <RequestResponseModal request={transactionsRequest} response={transactionsResponse} requestMetadata={requestMetadata} responseMetadata={responseMetadata} closeModal={closeModal} />
                                        </Modal>
                                        <ToolTip content="Please click on the Transaction ID to check further details" />
                                    </span>
                                    {showAmount(event.amount) && <span>Amount: {getSymbolFromCurrency(event.currency)}{event.amount}</span>}
                                    <span className='timeline-item-time'>{getDate(event.transactionDate)}</span>
                                </div>
                            </div>
                        </>
                    }
                </div>
            )}
        </>
    )
}
